import client from "./client";
import type { ChatMedia } from "../utils/chatMedia";

export interface LatestMessage {
  /** Stored id (absent on a live update) and the sender's local id: a deletion event matches either. */
  id?: string | null;
  local_id?: string | null;
  sender_uuid?: string | null;
  /** Deleted for everyone: the preview reads the placeholder. */
  deleted?: boolean;
  text?: string | null;
  /** Photos / videos of the last message (the full message JSON is stored). */
  attachment?: { media?: ChatMedia[] | null } | null;
  timeStamp?: number | null;
  time_stamp?: number | null;
}

export interface ConvUserSnapshot {
  userId: string;
  name: string;
  avatar?: string | null;
}

export interface CallerParticipant {
  convId: number;
  unreadCount?: number;
  lastReadMsgId?: string | null;
}

export interface Conversation {
  id: string;
  name: string;
  type: number | string; // number for old API (0=GC,1=TC1,2=TC2), string for new ("GROUP","PAIR",…)
  pubnubChannel: string;
  latestMessage?: LatestMessage | null;
  latestMsg?: LatestMessage | null;
  unreadCount?: number;
  userCount?: number;
  streetChatId?: string;
  userList?: ConvUserSnapshot[];
  callerParticipant?: CallerParticipant | null;
  owner?: { id?: string | null } | null;
  /** Who opened the GC: a street-chat GC shows as this person's first name (Android gcDisplayName). */
  creator?: { firstName?: string | null; name?: string | null } | null;
}

/**
 * A GC's display name: its creator's first name (Android `gcDisplayName`), else their full name. Never `name`, which
 * for a street-chat GC is an internal channel hash. Null when the creator is unknown.
 */
export function gcDisplayName(conv: Pick<Conversation, "creator">): string | null {
  return conv.creator?.firstName?.trim() || conv.creator?.name?.trim() || null;
}

export async function getConversations(
  page = 0,
  size = 50,
): Promise<Conversation[]> {
  const res = await client.get<{ result: Conversation[] }>("/room/api/conv", {
    params: { page, size },
  });
  return res.data.result ?? [];
}

export async function getPairConversations(
  page = 0,
  size = 20,
): Promise<Conversation[]> {
  const res = await client.get<{ result: Conversation[] }>(
    "/room/api/conv/pair",
    {
      params: { pageNum: page, pageSize: size },
    },
  );
  return res.data.result ?? [];
}

export async function createPairChat(
  targetUserId: string,
): Promise<Conversation> {
  const res = await client.post<{ result: Conversation }>("/api/conv/pair", {
    targetUserId,
  });
  return res.data.result;
}

export async function getConversation(convId: string): Promise<Conversation> {
  const res = await client.get<{ result: Conversation }>(
    `/room/api/conv/${convId}`,
  );
  return res.data.result;
}

export async function markConversationRead(
  convId: string,
  userId: string,
): Promise<void> {
  await client.post(`/api/conv/${convId}/user/${userId}/read-last-msg`);
}

export interface StreetChatCount {
  id: number;
  userCount: number;
  usersActiveIn5m: number;
  usersActiveIn12h: number;
}

export async function getStreetChatUserCount(
  stcId: number,
): Promise<StreetChatCount> {
  const res = await client.get<{ result: StreetChatCount }>(
    `/room/api/sc/${stcId}/user_count`,
  );
  return res.data.result;
}

export async function invitePerson(
  convId: number,
  userId: string,
  source: "tc1" | "tc2",
): Promise<void> {
  await client.post(
    "/api/conv/invite",
    new URLSearchParams({
      convId: String(convId),
      userId,
      source,
    }),
  );
}

export async function sendToTc1(targetUserId: string): Promise<Conversation> {
  const res = await client.post<{ result: Conversation }>(
    "/api/conv/send-to-tc1",
    new URLSearchParams({ targetUserId }),
  );
  return res.data.result;
}

export async function sendToTc2(targetUserId: string): Promise<Conversation> {
  const res = await client.post<{ result: Conversation }>(
    "/api/conv/send-to-tc2",
    new URLSearchParams({ targetUserId }),
  );
  return res.data.result;
}

export async function leaveConv(convId: number): Promise<void> {
  await client.post(
    "/api/conv/leave",
    new URLSearchParams({ convId: String(convId) }),
  );
}

export async function clearOffUser(
  convId: number,
  userId: string,
  banSecs: number | null,
): Promise<void> {
  await client.post(`/api/conv/${convId}/clear-off`, { userId, banSecs });
}

export interface GcChatSettings {
  enableChat: boolean;
  /** Slots are STC-audience: only users in slot.grantedUsers may publish. */
  byStc?: boolean | null;
  /** File/link slots are STC-audience: only granted users may cast (Android StreamInfoSetting.vmplByStc). */
  vmplBystc?: boolean | null;
}

export async function getGcChatSettings(
  stcId: number,
  gcId: number,
): Promise<GcChatSettings> {
  const res = await client.get<{ result: GcChatSettings }>(
    `/api/chat-settings/stc/${stcId}/gc/${gcId}`,
  );
  return res.data.result;
}

export async function getStcGroupConversations(
  stcId: number,
  pageNum = 0,
  pageSize = 10,
): Promise<Conversation[]> {
  const res = await client.get<{ result: Conversation[] }>(
    `/room/api/sc/${stcId}/conv`,
    {
      params: { pageNum, pageSize },
    },
  );
  return res.data.result ?? [];
}

export async function createGroupConv(scId: number): Promise<Conversation> {
  const res = await client.post<{ result: Conversation }>(
    `/api/sc/${scId}/conv`,
    { type: "GROUP" },
  );
  return res.data.result;
}

export async function joinWithMe(
  targetUserId: string,
  openDoor: boolean,
): Promise<Conversation> {
  const res = await client.post<{ result: Conversation }>(
    "/api/conv/join-with-me",
    {
      targetUserId,
      openDoor,
    },
  );
  return res.data.result;
}

export async function getGcHistory(
  pageNum?: number,
  pageSize?: number,
): Promise<Conversation[]> {
  const params =
    pageNum != null && pageSize != null ? { pageNum, pageSize } : undefined;
  const res = await client.get<{ result: Conversation[] }>(
    "/room/api/conv/gc-history",
    { params },
  );
  return res.data.result ?? [];
}

export interface StreetChatDetail { id: number; ownerId: string | null }

/** A street chat with its owner (room-stc `GET /api/sc/{scId}`; Android `getStc`). The owner is the host of every stream in it (spec 2026-10-19 §3.3). A NULL owner means no host. */
export async function getStreetChat(stcId: number): Promise<StreetChatDetail> {
  const res = await client.get<{ result: { id: number; owner?: { userId?: string | null; id?: string | null } | null } }>(`/room/api/sc/${stcId}`);
  const r = res.data.result;
  return { id: r.id, ownerId: r.owner?.userId ?? r.owner?.id ?? null };
}
